import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type { DemoCatalog, DemoCatalogEntry } from "@adva/types";

// Mirror of UploadedBackupPreview from @adva/services. The services package
// is server-only (it imports node:crypto, etc.) so we can't import its types
// directly into the browser bundle.
export interface UploadedBackupPreview {
  kind: "uploaded_backup";
  id: string;
  display_name: string;
  business: string;
  total_records: number;
  record_counts: Record<string, number>;
  exported_at: string;
  source_business_slug: string;
  schema_version: string;
}

interface RestoreFromBackupResponse {
  batch_id: string;
  job_count: number;
  status: "importing" | "completed" | "failed";
}

export function useDemoCatalog() {
  return useQuery({
    queryKey: ["demo-catalog"],
    queryFn: () => api.get<DemoCatalog>("/import/demo-catalog"),
    staleTime: 15 * 60 * 1000,
  });
}

export function useDemoCatalogEntry(id: string | undefined) {
  return useQuery({
    queryKey: ["demo-catalog", id],
    queryFn: () => api.get<DemoCatalogEntry>(`/import/demo-catalog/${id}`),
    enabled: Boolean(id),
    staleTime: 15 * 60 * 1000,
  });
}

interface ResetBusinessSummary {
  business_id: string;
  deleted: Record<string, number>;
  total_rows: number;
  tables_touched: number;
}

export function useResetBusiness() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<ResetBusinessSummary>("/import/reset-business", {}),
    onSuccess: () => {
      // Wipe every cached query — the user just nuked the operational data
      // tables, so any list/detail/aggregate is stale. invalidateQueries with
      // no key matches everything and triggers refetch on mount.
      queryClient.invalidateQueries();
    },
  });
}

export function useExportBusiness() {
  return useMutation({
    mutationFn: async () => {
      const blob = await api.download("/import/export-business");
      const filename = buildExportFilename();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return { filename };
    },
  });
}

// Server returns the canonical filename in the Content-Disposition header,
// but fetch() doesn't expose it without an extra round-trip; this name is a
// safe local fallback. Format mirrors export-business.ts:
//   `<slug>-backup-<iso>.zip`
// Without slug context, fall back to a generic timestamped name.
function buildExportFilename(): string {
  const stamp = new Date().toISOString().replace(/[:.]/g, "-");
  return `business-backup-${stamp}.zip`;
}

export function useInspectBackup() {
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", "inspect");
      return api.postMultipart<UploadedBackupPreview>(
        "/import/restore-from-backup",
        fd,
      );
    },
  });
}

export function useRestoreFromBackup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, resetFirst }: { file: File; resetFirst: boolean }) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", "execute");
      fd.append("reset_first", String(resetFirst));
      return api.postMultipart<RestoreFromBackupResponse>(
        "/import/restore-from-backup",
        fd,
      );
    },
    onSuccess: () => {
      // Restore mutates every operational table; nuke all cached queries.
      queryClient.invalidateQueries();
    },
  });
}
